import { t as uiText } from '../i18n';
// src/components/FileList.tsx
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useIsWorkspaceAdminQuery } from '../features/auth/queries/workspaceRoleQueries';
import { collection, getDocs, orderBy, query, doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { toRealDate } from '../lib/realDate';
import { ensureFilesCollection, FileRecord } from '../features/files/fileUtils';
import { formatFileSize } from '../features/workspace/fileStatsUtils';
import { getUserInfo, getMultipleUserInfo, formatUserName, UserInfo } from '../utils/userUtils';
import { queryKeys } from '../lib/queryKeys';
import { fetchFiles } from '../api/files';
import { fetchThreadTempFiles, type ThreadTempFileRecord } from '../api/threadTempFiles';
import { getFileTypeInfo, matchesFileTypeFilter } from '../utils/fileTypeUtils';
import JSZip from 'jszip';
import { workspacePagePath } from '../utils/workspaceNavigation';

interface FileListProps {
  isSelectionMode?: boolean;
  onSelectionChange?: (selectedIds: string[]) => void;
  selectedFileIds?: string[];
  refreshTrigger?: number;
  onSelectionModeToggle?: () => void;
  onDeleteClick?: () => void;
  isMobile?: boolean;
}

// ソート種別の定義
type SortType = 'name' | 'size' | 'usage' | 'created' | 'updated';
type SortOrder = 'asc' | 'desc';

// ファイルタイプフィルターの定義
type FileTypeFilter = 'all' | 'document' | 'spreadsheet' | 'presentation' | 'pdf' | 'text' | 'image' | 'other';

type UploadSourceFilter = 'all' | 'board' | 'thread';
type ThreadUploadKindFilter = 'all' | 'temporary' | 'permanent';

type UnifiedFileKind = 'permanent' | 'temporary';

type UnifiedFileItem = {
  kind: UnifiedFileKind;
  key: string; // `${kind}:${id}`
  id: string;
  title: string;
  mimeType: string;
  size: number;
  storagePath: string;
  downloadURL: string;
  createdAt: any;
  createdBy: string;
  updatedAt: any;
  updatedBy: string;
  referenceCount?: number;
  attachedBoardIds?: string[];
  attachedThreadRefs?: Array<{ threadId: string; targetType?: 'post' | 'comment'; targetId?: string; postId?: string }>;
  threadId?: string;
  postId?: string;
  expiresAt?: any;
  isDeleted?: boolean;
};

const badgeStyle = (opts: { bg: string; fg: string; border: string }): React.CSSProperties => ({
  fontSize: 11,
  padding: '2px 6px',
  borderRadius: 4,
  backgroundColor: opts.bg,
  color: opts.fg,
  border: `1px solid ${opts.border}`,
  whiteSpace: 'nowrap',
  flex: '0 0 auto',
});

const FileList: React.FC<FileListProps> = ({
  isSelectionMode = false,
  onSelectionChange,
  selectedFileIds = [],
  refreshTrigger = 0,
  onSelectionModeToggle,
  onDeleteClick,
  isMobile = false
}) => {
  const { workspaceId: routeWorkspaceId } = useParams();
  const { user } = useAuth();
  const { data: isWorkspaceAdmin = false } = useIsWorkspaceAdminQuery(
    routeWorkspaceId ?? '',
    !!routeWorkspaceId
  );
  const [sortType, setSortType] = useState<SortType>('updated');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [uploadSourceFilter, setUploadSourceFilter] = useState<UploadSourceFilter>('all');
  const [threadUploadKindFilter, setThreadUploadKindFilter] = useState<ThreadUploadKindFilter>('all');
  const [fileTypeFilter, setFileTypeFilter] = useState<FileTypeFilter>('all');
  const [showReferencePopup, setShowReferencePopup] = useState<string | null>(null);
  const [referencePages, setReferencePages] = useState<Array<{id: string, title: string, slug?: string}>>([]);
  const [userInfoMap, setUserInfoMap] = useState<Map<string, UserInfo>>(new Map());
  const [isDownloading, setIsDownloading] = useState(false);

  // TanStack Query使用
  const { 
    data: files = [], 
    isLoading: loading, 
    error 
  } = useQuery({
    queryKey: queryKeys.files.list(routeWorkspaceId ?? '', sortType, sortOrder, fileTypeFilter, refreshTrigger),
    queryFn: () => {
      if (import.meta.env.MODE === 'development') {}
      return (async () => {
        if (!routeWorkspaceId) return [] as any;
        const wsId = routeWorkspaceId;
        const [perm, temp] = await Promise.all([fetchFiles(wsId), fetchThreadTempFiles(wsId)]);
        const permItems: UnifiedFileItem[] = (perm as any[]).map((f) => ({
          kind: 'permanent',
          key: `permanent:${String(f.id)}`,
          id: String(f.id),
          title: String((f as any).title ?? ''),
          mimeType: String((f as any).mimeType ?? ''),
          size: typeof (f as any).size === 'number' ? (f as any).size : 0,
          storagePath: String((f as any).storagePath ?? ''),
          downloadURL: String((f as any).downloadURL ?? ''),
          createdAt: (f as any).createdAt,
          createdBy: String((f as any).createdBy ?? ''),
          updatedAt: (f as any).updatedAt,
          updatedBy: String((f as any).updatedBy ?? ''),
          referenceCount: typeof (f as any).referenceCount === 'number' ? (f as any).referenceCount : undefined,
          attachedBoardIds: Array.isArray((f as any).attachedBoardIds) ? (f as any).attachedBoardIds : [],
          attachedThreadRefs: Array.isArray((f as any).attachedThreadRefs) ? (f as any).attachedThreadRefs : [],
        }));
        const tempItems: UnifiedFileItem[] = (temp as ThreadTempFileRecord[])
          .filter((t) => !t.isDeleted)
          .map((t) => {
            const threadId = String((t as any).threadId ?? '');
            const targetType = (String((t as any).targetType ?? '') === 'comment' ? 'comment' : 'post') as
              | 'post'
              | 'comment';
            const targetId = String((t as any).targetId ?? (t as any).postId ?? '');
            return {
              kind: 'temporary',
              key: `temporary:${String(t.id)}`,
              id: String(t.id),
              title: String((t as any).fileName ?? ''),
              mimeType: String((t as any).mimeType ?? ''),
              size: typeof (t as any).size === 'number' ? (t as any).size : 0,
              storagePath: String((t as any).storagePath ?? ''),
              downloadURL: String((t as any).downloadURL ?? ''),
              createdAt: (t as any).createdAt,
              createdBy: String((t as any).createdBy ?? ''),
              updatedAt: (t as any).updatedAt ?? (t as any).createdAt,
              updatedBy: String((t as any).updatedBy ?? (t as any).createdBy ?? ''),
              referenceCount: 1,
              attachedBoardIds: [],
              attachedThreadRefs: [{ threadId, targetType, targetId }],
              threadId,
              postId: targetType === 'post' ? targetId : undefined,
              expiresAt: (t as any).expiresAt,
              isDeleted: Boolean((t as any).isDeleted),
            } satisfies UnifiedFileItem;
          });
        return [...permItems, ...tempItems] as any;
      })();
    },
    enabled: !!routeWorkspaceId,
  });

  const typedFiles = files as unknown as UnifiedFileItem[];

  // デバッグログ（開発環境のみ）
  if (import.meta.env.MODE === 'development') {}

  // ユーザー情報を取得（filesが変更された時のみ）
  useEffect(() => {
    const fetchUserInfos = async () => {
      if (!typedFiles.length) return;
      
      const userIds = Array.from(new Set(typedFiles.map(file => file.createdBy).filter(Boolean) as string[]));
      if (userIds.length > 0) {
        try {
          const userInfos = await getMultipleUserInfo(userIds);
          const newUserInfoMap = new Map<string, UserInfo>();
          userInfos.forEach(userInfo => {
            newUserInfoMap.set(userInfo.id, userInfo);
          });
          setUserInfoMap(newUserInfoMap);
        } catch (error) {
          console.error('ユーザー情報の取得エラー:', error);
        }
      }
    };

    fetchUserInfos();
  }, [typedFiles]);

  // 3. 実際のDOM要素の確認

  // ファイル削除権限をチェック
  const canDeleteFile = (file: UnifiedFileItem): boolean => {
    if (!user || !routeWorkspaceId) {
      return false;
    }

    // 管理者の場合は削除可能
    if (isWorkspaceAdmin === true) {
      return true;
    }

    // ファイル作成者の場合は削除可能
    if (file.createdBy === user.uid) {
      return true;
    }

    return false;
  };

  // 選択されたファイルの削除権限をチェック
  const canDeleteSelectedFiles = (): boolean => {
    if (!user || !routeWorkspaceId || selectedFileIds.length === 0) {
      return false;
    }

    // 管理者の場合は削除可能
    if (isWorkspaceAdmin === true) {
      return true;
    }

    // 選択されたファイルがすべて作成者の場合のみ削除可能
    const allFilesOwned = selectedFileIds.every(fileId => {
      const file = typedFiles.find(f => f.key === fileId);
      const isOwned = file && file.createdBy === user.uid;
      return isOwned;
    });

    return allFilesOwned;
  };

  // ファイルフィルター機能（新しいユーティリティ関数を使用）
  const filterFiles = (filesList: UnifiedFileItem[], typeFilter: FileTypeFilter): UnifiedFileItem[] => {
    if (typeFilter === 'all') return filesList;
    return filesList.filter(file => matchesFileTypeFilter(file.mimeType, typeFilter));
  };

  const filterByUploadSource = (filesList: UnifiedFileItem[], src: UploadSourceFilter): UnifiedFileItem[] => {
    if (src === 'all') return filesList;
    if (src === 'thread') {
      return filesList.filter((f) => {
        if (f.kind === 'temporary') return true;
        const threadCount = Array.isArray(f.attachedThreadRefs) ? f.attachedThreadRefs.length : 0;
        return threadCount > 0;
      });
    }
    // board
    return filesList.filter((f) => {
      const boardCount = Array.isArray(f.attachedBoardIds) ? f.attachedBoardIds.length : 0;
      return boardCount > 0;
    });
  };

  const filterThreadUploads = (filesList: UnifiedFileItem[], kind: ThreadUploadKindFilter): UnifiedFileItem[] => {
    if (kind === 'all') return filesList;
    if (kind === 'temporary') return filesList.filter((f) => f.kind === 'temporary');
    // permanent
    return filesList.filter((f) => {
      if (f.kind !== 'permanent') return false;
      const threadCount = Array.isArray(f.attachedThreadRefs) ? f.attachedThreadRefs.length : 0;
      return threadCount > 0;
    });
  };

  // ファイルソート機能
  const sortFiles = (filesList: UnifiedFileItem[], type: SortType, order: SortOrder): UnifiedFileItem[] => {
    return [...filesList].sort((a, b) => {
      let compareResult = 0;
      const usageCount = (f: UnifiedFileItem) => {
        const boards = Array.isArray(f.attachedBoardIds) ? f.attachedBoardIds.length : 0;
        const threads = Array.isArray(f.attachedThreadRefs) ? f.attachedThreadRefs.length : 0;
        return boards + threads;
      };
      
      switch (type) {
        case 'name':
          compareResult = a.title.localeCompare(b.title, 'ja');
          break;
        case 'size':
          compareResult = a.size - b.size;
          break;
        case 'usage':
          compareResult = usageCount(a) - usageCount(b);
          break;
        case 'created':
          const aCreated = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const bCreated = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          compareResult = aCreated.getTime() - bCreated.getTime();
          break;
        case 'updated':
        default:
          const aUpdated = a.updatedAt?.toDate ? a.updatedAt.toDate() : new Date(a.updatedAt);
          const bUpdated = b.updatedAt?.toDate ? b.updatedAt.toDate() : new Date(b.updatedAt);
          compareResult = aUpdated.getTime() - bUpdated.getTime();
          break;
      }
      
      return order === 'asc' ? compareResult : -compareResult;
    });
  };

  const getAttachmentBadges = (file: UnifiedFileItem): Array<{ key: string; label: string; style: React.CSSProperties }> => {
    const badges: Array<{ key: string; label: string; style: React.CSSProperties }> = [];
    const boardCount = Array.isArray(file.attachedBoardIds) ? file.attachedBoardIds.length : 0;
    const threadCount = Array.isArray(file.attachedThreadRefs) ? file.attachedThreadRefs.length : 0;

    if (file.kind === 'temporary') {
      badges.push({ key: 'thread', label: uiText("Thread"), style: badgeStyle({ bg: '#f5f3ff', fg: '#5b21b6', border: '#ddd6fe' }) });
      badges.push({ key: 'temp', label: uiText("Temporary"), style: badgeStyle({ bg: '#fff3e6', fg: '#8a4b10', border: '#f6d5b5' }) });
      return badges;
    }

    if (boardCount > 0) {
      badges.push({
        key: 'board',
        label: boardCount > 1 ? uiText("Pages ({{boardCount}})", { boardCount: boardCount }) : uiText("Page"),
        style: badgeStyle({ bg: '#eef2ff', fg: '#3b4aa6', border: '#cfd8ff' }),
      });
    }
    if (threadCount > 0) {
      badges.push({
        key: 'thread',
        label: threadCount > 1 ? uiText("Threads ({{threadCount}})", { threadCount: threadCount }) : uiText("Thread"),
        style: badgeStyle({ bg: '#f5f3ff', fg: '#5b21b6', border: '#ddd6fe' }),
      });
    }
    return badges;
  };

  // フィルターとソートを適用
  const bySource = filterByUploadSource(typedFiles, uploadSourceFilter);
  const byThreadKind =
    uploadSourceFilter === 'thread' ? filterThreadUploads(bySource, threadUploadKindFilter) : bySource;
  const filteredFiles = filterFiles(byThreadKind, fileTypeFilter);
  const sortedFiles = sortFiles(filteredFiles, sortType, sortOrder);

  // ソート変更ハンドラー
  const handleSortChange = (type: SortType) => {
    if (sortType === type) {
      // 同じソート項目の場合は昇順/降順を切り替え
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // 異なるソート項目の場合は新しい項目を設定し、デフォルトの順序にする
      setSortType(type);
      // 名前は昇順、それ以外は降順をデフォルトにする
      setSortOrder(type === 'name' ? 'asc' : 'desc');
    }
  };

  // 選択状態の管理
  const handleFileSelection = (fileId: string, checked: boolean) => {
    if (!onSelectionChange) return;

    let newSelection: string[];
    if (checked) {
      newSelection = [...selectedFileIds, fileId];
    } else {
      newSelection = selectedFileIds.filter(id => id !== fileId);
    }

    onSelectionChange(newSelection);
  };

  // 参照ページ/スレッド情報を取得
  const fetchReferencePages = async (item: UnifiedFileItem) => {
    if (!routeWorkspaceId) {
      setReferencePages([]);
      return;
    }

    const boardIds = item.attachedBoardIds || [];
    const threadRefs = item.attachedThreadRefs || [];
    if (boardIds.length === 0 && threadRefs.length === 0) {
      setReferencePages([]);
      return;
    }

    try {
      const boardPages = await Promise.all(
        boardIds.map(async (boardId) => {
          const boardRef = doc(db, `workspaces/${routeWorkspaceId}/boards`, boardId);
          const boardSnap = await getDoc(boardRef);

          if (boardSnap.exists()) {
            const data = boardSnap.data();
            const title =
              data.searchData?.originalTitle ||
              data.searchData?.normalizedTitle ||
              data.editData?.title ||
              data.title ||
              data.slug ||
              uiText("Untitled page");

            return {
              id: `board:${boardId}`,
              title: uiText("Page: {{title}}", { title: title }),
              slug: data.slug,
            };
          }

          return { id: `board:${boardId}`, title: uiText("Page: (deleted page)"), slug: undefined };
        })
      );

      const uniqueThreadIds = Array.from(new Set(threadRefs.map((r) => r.threadId).filter(Boolean)));
      const threadPages = await Promise.all(
        uniqueThreadIds.map(async (threadId) => {
          const refDoc = doc(db, `workspaces/${routeWorkspaceId}/threads`, threadId);
          const snap = await getDoc(refDoc);
          if (snap.exists()) {
            const data = snap.data() as any;
            const title = String(data?.title ?? uiText("(Untitled)"));
            return { id: `thread:${threadId}`, title: uiText("Thread: {{title}}", { title: title }), slug: undefined };
          }
          return { id: `thread:${threadId}`, title: uiText("Thread: (deleted thread)"), slug: undefined };
        })
      );

      setReferencePages([...boardPages, ...threadPages]);
    } catch (error) {
      console.error('参照情報の取得エラー:', error);
      setReferencePages([]);
    }
  };

  // 参照ページボタンのクリックハンドラー
  const handleShowReferencePages = async (item: UnifiedFileItem) => {
    if (showReferencePopup === item.key) {
      setShowReferencePopup(null);
      setReferencePages([]);
    } else {
      setShowReferencePopup(item.key);
      await fetchReferencePages(item);
    }
  };

  // ポップアップ外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = () => {
      setShowReferencePopup(null);
      setReferencePages([]);
    };

    if (showReferencePopup) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showReferencePopup]);

  // 全選択/全解除
  const handleSelectAll = (checked: boolean) => {
    if (!onSelectionChange) return;

    if (checked) {
      const allFileIds = sortedFiles.map(file => file.key);
      onSelectionChange(allFileIds);
    } else {
      onSelectionChange([]);
    }
  };

  // 一括ダウンロード機能
  const handleBulkDownload = async () => {
    if (selectedFileIds.length === 0) {
      alert(uiText("Select files to download."));
      return;
    }

    setIsDownloading(true);
    
    try {
      const zip = new JSZip();
      const selectedFiles = typedFiles.filter(file => selectedFileIds.includes(file.key));
      let successCount = 0;
      let failedFiles: string[] = [];

      // 各ファイルをZIPに追加
      for (const file of selectedFiles) {
        try {
          const response = await fetch(file.downloadURL, {
            mode: 'cors',
            credentials: 'omit'
          });

          if (!response.ok) {
            console.warn('📦 [FileList] ファイル取得失敗:', {
              fileName: file.title,
              status: response.status
            });
            failedFiles.push(file.title);
            continue;
          }

          const blob = await response.blob();

          // ファイル名の重複を避けるため、必要に応じて番号を付加
          let fileName = file.title;
          let counter = 1;
          while (zip.file(fileName)) {
            const ext = fileName.split('.').pop();
            const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
            fileName = `${nameWithoutExt}_${counter}.${ext}`;
            counter++;
          }

          zip.file(fileName, blob);
          successCount++;
        } catch (error) {
          console.error('📦 [FileList] ファイル取得エラー:', {
            fileName: file.title,
            error
          });
          failedFiles.push(file.title);
        }
      }

      // 成功したファイルがない場合の処理
      if (successCount === 0) {
        alert(uiText("Failed to get the file. CORS settings may still be propagating.\n\nDownload files individually, or try again later."));
        setIsDownloading(false);
        return;
      }

      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });

      // ダウンロード実行
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `files_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // 結果メッセージの表示
      if (failedFiles.length === 0) {
        alert(uiText("Downloaded {{successCount}} files as a ZIP.", { successCount: successCount }));
      } else {
        alert(uiText("Downloaded {{successCount}} files as a ZIP.\n\nThese files could not be retrieved:\n{{v1}}\n\nDownload these files individually.", { successCount: successCount, v1: failedFiles.join('\n') }));
      }
    } catch (error) {
      console.error('📦 [FileList] 一括ダウンロードエラー:', error);
      alert(uiText("An error occurred during the bulk download.\n\nCORS settings may still be taking effect. Wait a while and try again, or download files individually."));
    } finally {
      setIsDownloading(false);
    }
  };

  // ファイルサイズの表示
  const renderFileSize = (size: number) => {
    return formatFileSize(size);
  };

  // ファイルタイプの判定とアイコン表示（新しいユーティリティ関数を使用）
  const getFileIcon = (mimeType: string, fileName?: string) => {
    return getFileTypeInfo(mimeType, fileName).icon;
  };

  // 最終更新日時の表示
  const formatDate = (timestamp: any) => {
    const date = toRealDate(timestamp);
    if (!date) return '';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return uiText("Today {{v0}}", { v0: date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' }) });
    } else if (diffDays === 1) {
      return uiText("Yesterday {{v0}}", { v0: date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' }) });
    } else if (diffDays < 7) {
      return uiText("{{diffDays}} days ago", { diffDays: diffDays });
    } else {
      return date.toLocaleDateString('ja-JP');
    }
  };

  // エラーハンドリング
  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '16px', color: '#dc3545' }}>
          {uiText("Failed to load file")}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '16px', color: '#666' }}>
          {uiText("Loading file...")}
        </div>
      </div>
    );
  }

  // ファイルがない場合のメッセージコンポーネント
  const EmptyFileMessage = () => {
    const isFiltered = fileTypeFilter !== 'all';
    
    if (typedFiles.length === 0) {
      // ファイルが1つもない場合
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '18px', color: '#999', marginBottom: '8px' }}>
             {uiText("No files")}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            {uiText("Files you attach on a page are listed here")}
          </div>
        </div>
      );
    } else if (isFiltered && sortedFiles.length === 0) {
      // フィルター結果で該当ファイルがない場合
      const filterLabels: Record<FileTypeFilter, string> = {
        all: uiText("Everything"),
        pdf: 'PDF',
        document: uiText("Document"),
        spreadsheet: uiText("Spreadsheet"),
        presentation: uiText("Presentation"),
        text: uiText("Text"),
        image: uiText("Image"),
        other: uiText("Other")
      };
      
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '18px', color: '#999', marginBottom: '8px' }}>
            {uiText("No matching files")}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            「{filterLabels[fileTypeFilter]}{uiText("\": no files of this type")}
          </div>
        </div>
      );
    }
    
    return null;
  };

  return (
    <div style={{ padding: '0' }}>
      {/* アップロード元フィルター */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? '8px' : '16px',
        marginBottom: '12px',
        padding: '12px 16px',
        backgroundColor: '#fff',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <span style={{ fontSize: '14px', fontWeight: '500', color: '#495057' }}>
          {uiText("Upload:")}
        </span>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { key: 'all', label: uiText("Everything") },
            { key: 'board', label: uiText("Page") },
            { key: 'thread', label: uiText("Thread") },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => {
                const next = key as UploadSourceFilter;
                setUploadSourceFilter(next);
                if (next !== 'thread') setThreadUploadKindFilter('all');
              }}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                borderRadius: '16px',
                border: '1px solid var(--workspace-primary, #cfae91)',
                backgroundColor: uploadSourceFilter === key ? 'var(--workspace-primary, #cfae91)' : '#fff',
                color: uploadSourceFilter === key ? '#fff' : 'var(--workspace-primary, #cfae91)',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (uploadSourceFilter !== key) {
                  e.currentTarget.style.backgroundColor = '#f8f5f0';
                }
              }}
              onMouseLeave={(e) => {
                if (uploadSourceFilter !== key) {
                  e.currentTarget.style.backgroundColor = '#fff';
                }
              }}
            >
              {label}
            </button>
          ))}

          {uploadSourceFilter === 'thread' && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              {[
                { key: 'all', label: uiText("Everything") },
                { key: 'temporary', label: uiText("Temporary") },
                { key: 'permanent', label: uiText("Permanent") },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setThreadUploadKindFilter(key as ThreadUploadKindFilter)}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    borderRadius: '16px',
                    border: '1px solid var(--workspace-primary, #cfae91)',
                    backgroundColor: threadUploadKindFilter === key ? 'var(--workspace-primary, #cfae91)' : '#fff',
                    color: threadUploadKindFilter === key ? '#fff' : 'var(--workspace-primary, #cfae91)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (threadUploadKindFilter !== key) {
                      e.currentTarget.style.backgroundColor = '#f8f5f0';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (threadUploadKindFilter !== key) {
                      e.currentTarget.style.backgroundColor = '#fff';
                    }
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ファイルタイプフィルター */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? '8px' : '16px',
        marginBottom: '12px',
        padding: '12px 16px',
        backgroundColor: '#fff',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <span style={{ fontSize: '14px', fontWeight: '500', color: '#495057' }}>
          {uiText("File type:")}
        </span>
        
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: uiText("Everything") },
            { key: 'pdf', label: 'PDF' },
            { key: 'document', label: uiText("Document") },
            { key: 'spreadsheet', label: uiText("Spreadsheet") },
            { key: 'presentation', label: uiText("Presentation") },
            { key: 'text', label: uiText("Text") },
            { key: 'image', label: uiText("Image") },
            { key: 'other', label: uiText("Other") }
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFileTypeFilter(key as FileTypeFilter)}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                borderRadius: '16px',
                border: '1px solid var(--workspace-primary, #cfae91)',
                backgroundColor: fileTypeFilter === key ? 'var(--workspace-primary, #cfae91)' : '#fff',
                color: fileTypeFilter === key ? '#fff' : 'var(--workspace-primary, #cfae91)',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (fileTypeFilter !== key) {
                  e.currentTarget.style.backgroundColor = '#f8f5f0';
                }
              }}
              onMouseLeave={(e) => {
                if (fileTypeFilter !== key) {
                  e.currentTarget.style.backgroundColor = '#fff';
                }
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {/* ソート機能 */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? '8px' : '16px',
        marginBottom: '16px',
        padding: '12px 16px',
        backgroundColor: '#fff',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <span style={{ fontSize: '14px', fontWeight: '500', color: '#495057' }}>
          {uiText("Sort:")}
        </span>
        
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {[
            { key: 'name', label: uiText("Name") },
            { key: 'size', label: uiText("Size") },
            { key: 'usage', label: uiText("Pages used") },
            { key: 'created', label: uiText("Registered on") },
            { key: 'updated', label: uiText("Updated date") }
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => handleSortChange(key as SortType)}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                borderRadius: '16px',
                border: '1px solid var(--workspace-primary, #cfae91)',
                backgroundColor: sortType === key ? 'var(--workspace-primary, #cfae91)' : '#fff',
                color: sortType === key ? '#fff' : 'var(--workspace-primary, #cfae91)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              onMouseEnter={(e) => {
                if (sortType !== key) {
                  e.currentTarget.style.backgroundColor = '#f8f5f0';
                }
              }}
              onMouseLeave={(e) => {
                if (sortType !== key) {
                  e.currentTarget.style.backgroundColor = '#fff';
                }
              }}
            >
              {label}
              {sortType === key && (
                <span style={{ fontSize: '10px' }}>
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      {/* 選択コントロールエリア */}
      {onSelectionModeToggle && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          padding: '12px 16px',
          backgroundColor: isSelectionMode ? '#f8f9fa' : '#fff',
          borderRadius: '8px',
          marginBottom: '16px',
          border: '1px solid #e9ecef'
        }}>
          {isSelectionMode ? (
            <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#495057', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selectedFileIds.length === sortedFiles.length && sortedFiles.length > 0}
                onChange={(e) => handleSelectAll(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              {uiText("Selected count:")} {selectedFileIds.length}/{sortedFiles.length}
            </label>
          ) : (
            <div style={{ fontSize: '14px', color: '#666' }}>
              {uiText("Selected: 0 items")}
            </div>
          )}
          
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row', 
            gap: isMobile ? '6px' : '8px', 
            alignItems: 'flex-end' 
          }}>
            {/* 選択中止ボタン（スマホ時は一番上、PC時は一番右） */}
            <button
              onClick={onSelectionModeToggle}
              style={{
                padding: isMobile ? '6px 12px' : '8px 16px',
                backgroundColor: isSelectionMode ? '#6c757d' : 'var(--workspace-primary, #cfae91)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: isMobile ? '12px' : '14px',
                minWidth: isMobile ? '80px' : 'auto',
                order: isMobile ? 1 : 3
              }}
            >
              {isSelectionMode ? uiText("Cancel selection") : uiText("Select")}
            </button>
            
            {/* アクションボタン（選択モード時のみ表示） */}
            {isSelectionMode && selectedFileIds.length > 0 && (
              <>
                <button
                  style={{
                    padding: isMobile ? '6px 12px' : '8px 16px',
                    backgroundColor: isDownloading ? '#6c757d' : 'var(--workspace-primary, #cfae91)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isDownloading ? 'not-allowed' : 'pointer',
                    fontSize: isMobile ? '12px' : '14px',
                    minWidth: isMobile ? '80px' : 'auto',
                    order: isMobile ? 2 : 2,
                    opacity: isDownloading ? 0.7 : 1
                  }}
                  onClick={handleBulkDownload}
                  disabled={isDownloading}
                >
                  {isDownloading ? uiText("Downloading...") : uiText("Download all")}
                </button>
                
                {(() => {
                  const canDelete = canDeleteSelectedFiles();
                  return canDelete;
                })() && (
                  <button
                    style={{
                      padding: isMobile ? '6px 12px' : '8px 16px',
                      backgroundColor: '#f8d7da',
                      color: '#721c24',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: isMobile ? '12px' : '14px',
                      minWidth: isMobile ? '80px' : 'auto',
                      order: isMobile ? 3 : 1
                    }}
                    onClick={() => {
                      if (onDeleteClick) {
                        onDeleteClick();
                      }
                    }}
                  >
                    {uiText("Delete")}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {/* ファイル一覧またはメッセージ */}
      {sortedFiles.length === 0 ? (
        <EmptyFileMessage />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {sortedFiles.map((file) => (
          <div
            key={file.key}
            style={{
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: '16px',
              backgroundColor: selectedFileIds.includes(file.key) ? '#e3f2fd' : '#fff',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              width: '100%',
              boxSizing: 'border-box'
            }}
            onMouseEnter={(e) => {
              if (!selectedFileIds.includes(file.key)) {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
              }
            }}
            onMouseLeave={(e) => {
              if (!selectedFileIds.includes(file.key)) {
                e.currentTarget.style.backgroundColor = '#fff';
              }
            }}
          >
            <div>
              {/* ファイル情報全体 */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                {/* 左側: ファイル基本情報 */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  flex: 1,
                  minWidth: 0,
                  width: isMobile ? '100%' : 'auto'
                }}>
                  {/* 選択チェックボックス */}
                  {isSelectionMode && (
                    <input
                      type="checkbox"
                      checked={selectedFileIds.includes(file.key)}
                      onChange={(e) => handleFileSelection(file.key, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}

                  {/* ファイルアイコン */}
                  <div style={{ flexShrink: 0 }}>
                    {(() => {
                      const fileTypeInfo = getFileTypeInfo(file.mimeType, file.title);
                      return (
                        <div
                          style={{
                            backgroundColor: fileTypeInfo.backgroundColor,
                            color: fileTypeInfo.color,
                            width: '40px',
                            height: '40px',
                            borderRadius: '6px',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            letterSpacing: '0.3px'
                          }}
                        >
                          {fileTypeInfo.text}
                        </div>
                      );
                    })()}
                  </div>

                  {/* ファイル情報 */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '500',
                      color: '#333',
                      marginBottom: '4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.4',
                      maxHeight: '2.8em'
                    }}>
                      {file.title}
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobile ? '8px' : '16px',
                      fontSize: '14px',
                      color: '#666',
                      flexWrap: isMobile ? 'wrap' : 'nowrap'
                    }}>
                      <span>{renderFileSize(file.size)}</span>
                      {getAttachmentBadges(file).length > 0 && (
                        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          {getAttachmentBadges(file).map((b) => (
                            <span key={b.key} style={b.style}>
                              {b.label}
                            </span>
                          ))}
                        </span>
                      )}
                      <span>{uiText("References:")} {file.referenceCount || 0}</span>
                      {!isMobile && formatDate(file.createdAt) && <span>{uiText("Registered on:")} {formatDate(file.createdAt)}</span>}
                      {formatDate(file.updatedAt) && <span>{uiText("Updated on:")} {formatDate(file.updatedAt)}</span>}
                      <span>{uiText("Created by:")} {formatUserName(userInfoMap.get(file.createdBy) || null)}</span>
                    </div>
                  </div>
                </div>

                {/* 右側: ボタングループ */}
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'row',
                  gap: '8px', 
                  position: 'relative',
                  flexShrink: 0,
                  width: isMobile ? '100%' : 'auto'
                }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShowReferencePages(file);
                  }}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: showReferencePopup === file.key ? 'var(--workspace-primary, #cfae91)' : 'var(--workspace-primary, #cfae91)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    flex: isMobile ? 1 : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (showReferencePopup !== file.key) {
                      e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (showReferencePopup !== file.key) {
                      e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
                    }
                  }}
                >
                  {uiText("Referenced page")}
                </button>

                {/* 参照ページポップアップ */}
                {showReferencePopup === file.key && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: isMobile ? '50%' : 'auto',
                      right: isMobile ? 'auto' : '0',
                      transform: isMobile ? 'translateX(-50%)' : 'none',
                      marginTop: '4px',
                      backgroundColor: 'white',
                      border: '1px solid #e9ecef',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 9999,
                      width: isMobile ? 'calc(100vw - 32px)' : 'auto',
                      maxWidth: isMobile ? 'none' : '640px',
                      minWidth: isMobile ? 'auto' : '320px'
                    }}
                  >
                    <div style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #e9ecef',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#495057'
                    }}>
                      {uiText("Referenced pages (")}{referencePages.length}{uiText(" items)")}
                    </div>
                    
                    {referencePages.length === 0 ? (
                      <div style={{
                        padding: '16px',
                        textAlign: 'center',
                        color: '#999',
                        fontSize: '14px'
                      }}>
                        {uiText("No referenced pages")}
                      </div>
                    ) : (
                      <div style={{ maxHeight: isMobile ? '150px' : '200px', overflowY: 'auto' }}>
                        {referencePages.map((page) => (
                          <div
                            key={page.id}
                            style={{
                              padding: isMobile ? '12px 16px' : '8px 16px',
                              borderBottom: '1px solid #f8f9fa',
                              cursor: 'pointer',
                              fontSize: isMobile ? '16px' : '14px',
                              color: '#333',
                              transition: 'background-color 0.2s ease',
                              lineHeight: '1.4'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                            onClick={() => {
                              const rawId = String(page.id);
                              if (!routeWorkspaceId) return;
                              if (rawId.startsWith('board:')) {
                                const boardId = rawId.slice('board:'.length);
                                const linkPath = page.slug
                                  ? workspacePagePath(routeWorkspaceId, page.slug)
                                  : workspacePagePath(routeWorkspaceId, boardId);
                                window.open(linkPath, '_blank');
                              } else if (rawId.startsWith('thread:')) {
                                const threadId = rawId.slice('thread:'.length);
                                const params = new URLSearchParams(window.location.search);
                                params.set('tab', 'thread');
                                params.set('threadId', threadId);
                                const linkPath = `/${routeWorkspaceId}?${params.toString()}`;
                                window.open(linkPath, '_blank');
                              }
                              setShowReferencePopup(null);
                              setReferencePages([]);
                            }}
                          >
                            {page.title}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                <a
                  href={file.downloadURL}
                  download={file.title}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    display: 'inline-block',
                    padding: '6px 12px',
                    backgroundColor: 'var(--workspace-primary, #cfae91)',
                    color: 'white',
                    textDecoration: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    transition: 'background-color 0.2s ease',
                    flex: isMobile ? 1 : 'none',
                    textAlign: 'center',
                    boxSizing: 'border-box'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
                  }}
                >
                  {uiText("Download")}
                </a>
                </div>
              </div>
            </div>
          </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileList;
